import React, { useEffect, useState, useRef } from "react";
import { StickyNote, User, Send, Clock } from "lucide-react";

const NotesChat = () => {
  const [notes, setNotes] = useState([]);
  const [noteInput, setNoteInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  const user = JSON.parse(localStorage.getItem("user"));
  const studentId = localStorage.getItem("student_id");

  const fetchNotes = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/get-notes/${studentId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch notes");
      const data = await response.json();
      setNotes(data);
      setError(null);
    } catch (err) {
      console.error("Fetch notes error:", err);
      setError("Unable to load notes.");
    }
  };

  useEffect(() => {
    if (!studentId) {
      setError("Student ID not found.");
      return;
    }

    fetchNotes();
    const interval = setInterval(fetchNotes, 5000);
    return () => clearInterval(interval);
  }, [studentId]);

  const sendNote = async () => {
    const trimmedNote = noteInput.trim();
    if (!trimmedNote) {
      setError("Please enter a note.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/add-note/${studentId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            note: trimmedNote,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to send note");

      setNoteInput("");
      await fetchNotes();
    } catch (err) {
      console.error("Send note error:", err);
      setError("Failed to send note");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (datetime) => {
    const date = new Date(datetime);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (datetime) => {
    const date = new Date(datetime);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year:
          date.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
      });
    }
  };

  // Group notes by date
  const groupedNotes = notes.reduce((groups, note) => {
    const date = new Date(note.created_at).toDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(note);
    return groups;
  }, {});

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [notes]);

  return (
    <div className="flex flex-col h-[500px] sm:h-[600px] bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <StickyNote size={18} className="sm:w-5 sm:h-5 text-white" />
          </div>
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-white">
              Student Notes
            </h2>
            <p className="text-blue-100 text-xs sm:text-sm">
              All notes are saved automatically
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5 bg-white/20 rounded-full px-2 py-1 sm:px-3 sm:py-1">
          <User size={14} className="sm:w-4 sm:h-4 text-white" />
          <span className="text-white text-xs sm:text-sm font-medium truncate max-w-20 sm:max-w-none">
            {studentId || "Unknown"}
          </span>
        </div>
      </div>

      {/* Notes Display */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-4 bg-gradient-to-b from-gray-50 to-blue-50/20">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 flex items-start gap-2">
            <div className="bg-red-100 p-1 rounded-full flex-shrink-0 mt-0.5">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-red-600"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-red-700 text-sm">
                Couldn't load notes
              </h3>
              <p className="text-red-600 text-xs">{error}</p>
            </div>
          </div>
        )}

        {notes.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="mb-4 bg-blue-100 p-3 sm:p-4 rounded-full">
              <StickyNote size={28} className="sm:w-8 sm:h-8 text-blue-700" />
            </div>
            <h3 className="text-lg sm:text-xl font-medium text-gray-700 mb-2">
              No notes yet
            </h3>
            <p className="text-gray-500 text-sm max-w-xs">
              Add your first note to track important information about this student
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedNotes).map(([date, dateNotes]) => (
              <div key={date} className="space-y-3">
                <div className="relative flex items-center justify-center">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative bg-white px-3 py-1 rounded-full border border-blue-200 text-xs font-medium text-blue-600">
                    {date}
                  </div>
                </div>
                {dateNotes.map((note) => (
                  <div key={note.id} className="flex items-start gap-2 sm:gap-3">
                    <div className="bg-blue-100 w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <User size={14} className="sm:w-4 sm:h-4 text-blue-700" />
                    </div>
                    <div className="bg-white p-3 sm:p-4 rounded-lg shadow-xs border border-gray-200 max-w-[calc(100%-3rem)] sm:max-w-[85%] relative flex-1">
                      <div className="absolute -left-1.5 sm:-left-2 top-3 sm:top-4 w-3 h-3 sm:w-4 sm:h-4 bg-white border-l border-t border-gray-200 transform rotate-45"></div>
                      <p className="text-gray-800 text-sm sm:text-base leading-relaxed break-words">
                        {note.note}
                      </p>
                      <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-2">
                        <Clock size={12} className="flex-shrink-0" />
                        <span>{formatTime(note.created_at)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-gray-200 p-3 sm:p-4 bg-white">
        <div className="relative">
          <textarea
            rows={2}
            maxLength={500}
            className="w-full px-3 sm:px-4 py-2.5 pr-12 sm:pr-14 bg-gray-50 rounded-lg sm:rounded-xl border border-gray-300 focus:outline-none focus:ring-1 sm:focus:ring-2 focus:ring-blue-500 resize-none shadow-xs text-sm sm:text-base"
            placeholder="Add a note about this student..."
            value={noteInput}
            onChange={(e) => setNoteInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !loading) {
                e.preventDefault();
                sendNote();
              }
            }}
            disabled={loading}
          />
          <div className="absolute right-2 sm:right-3 bottom-2 sm:bottom-3 flex items-center gap-1.5 sm:gap-2">
            <span
              className={`text-xs ${
                noteInput.length > 450 ? "text-red-500" : "text-gray-400"
              }`}
            >
              {noteInput.length}/500
            </span>
            <button
              onClick={sendNote}
              disabled={loading || !noteInput.trim()}
              className={`p-1.5 sm:p-2 rounded-full flex items-center justify-center transition-all ${
                loading || !noteInput.trim()
                  ? "text-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:shadow-sm hover:scale-105"
              }`}
            >
              {loading ? (
                <svg
                  className="animate-spin h-4 w-4 sm:h-5 sm:w-5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              ) : (
                <Send size={16} className="sm:w-5 sm:h-5" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotesChat;